import { useEffect, useState } from 'react';

export type TravelerPhotos = Readonly<Record<string, string | null>>;

/** Reads the photo of each traveler id from the host; the URL is pre-signed and expires
 *  (#2640), so it never travels in a view. */
export type PhotosFor = (ids: readonly string[]) => Promise<TravelerPhotos>;

/**
 * The photo of each traveler id, asked from the host at draw time.
 *
 * Fail-soft: without `photosFor`, with the read in flight or failing, the id has no photo and
 * the avatar keeps the initials. Only the photos of the ids in question come out — the answer
 * for a previous list does not leak into the new one.
 */
export function useTravelerPhotos(
  ids: readonly string[],
  photosFor: PhotosFor | undefined,
): TravelerPhotos {
  const key = ids.join(',');
  // The source enters the mark of what was read: with another source (another identity in the
  // host), the photos read by the previous one do not serve while the new read is pending.
  const [loaded, setLoaded] = useState<{
    key: string;
    source?: PhotosFor;
    photos: TravelerPhotos;
  }>({ key: '', photos: {} });
  useEffect(() => {
    if (!photosFor || !key) return undefined;
    let cancelled = false;
    void photosFor(key.split(','))
      .then((photos) => {
        if (!cancelled) setLoaded({ key, source: photosFor, photos });
      })
      .catch(() => {
        // A photo is an ornament: the failure keeps the initials, never an error on the card.
        if (!cancelled) setLoaded({ key, source: photosFor, photos: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [key, photosFor]);
  return photosFor && loaded.source === photosFor && loaded.key === key ? loaded.photos : {};
}
